import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import apiClient from '../utils/api';

const PasswordResetContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const PasswordResetCard = styled.div`
  background: white;
  padding: 3rem 2.5rem;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 480px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
    border-radius: 20px;
    max-width: 100%;
  }
`;

const Title = styled.h1`
  color: #000000;
  margin-bottom: 2rem;
  font-size: 2rem;
  text-align: center;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1.75rem;
  }
`;

const Notice = styled.div`
  background: #e7f5ff;
  border: 1px solid #339af0;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 2rem;
  text-align: left;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const NoticeText = styled.div`
  font-size: 0.875rem;
  color: #1971c2;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  text-align: left;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  padding: 0.75rem;
  background: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 8px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.625rem;
  }
`;

const SuccessMessage = styled.div`
  color: #0f5132;
  font-size: 0.9rem;
  padding: 0.75rem;
  background: #d1e7dd;
  border: 1px solid #badbcc;
  border-radius: 8px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.625rem;
  }
`;

const BackButton = styled.button`
  margin-top: 1.5rem;
  background: none;
  border: none;
  color: #6c757d;
  font-size: 0.9375rem;
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    color: #000000;
  }
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const PasswordReset: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'verify' | 'reset'>('verify');
  const [verifyData, setVerifyData] = useState({
    user_id: '',
    name: '',
    phone: '',
    birthDate: ''
  });
  const [resetData, setResetData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    const limited = numbers.slice(0, 11);
    
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 7) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7)}`;
    }
  };

  const handleVerifyInputChange = (field: string, value: string) => {
    let formattedValue = value;
    
    if (field === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }
    
    if (field === 'birthDate') {
      const numbers = value.replace(/[^\d]/g, '');
      formattedValue = numbers.slice(0, 6);
    }
    
    setVerifyData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
    setError('');
    setSuccess('');
  };

  const handleResetInputChange = (field: string, value: string) => {
    setResetData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verifyData.user_id.trim()) {
      setError('아이디를 입력해주세요.');
      return;
    }
    
    if (!verifyData.name.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    
    if (!verifyData.phone.trim()) {
      setError('전화번호를 입력해주세요.');
      return;
    }
    
    if (!verifyData.birthDate.trim()) {
      setError('생년월일을 입력해주세요.');
      return;
    }
    
    if (verifyData.birthDate.length !== 6) {
      setError('생년월일은 6자리로 입력해주세요. (예: 231105)');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // 전화번호에서 하이픈 제거
      const phoneWithoutHyphen = verifyData.phone.replace(/-/g, '');
      
      const response = await apiClient.post('/auth/verify-password-reset', {
        user_id: verifyData.user_id,
        name: verifyData.name,
        phone: phoneWithoutHyphen,
        birth_date: verifyData.birthDate
      });
      
      if (response.data.success) {
        setStep('reset');
        setSuccess('');
        setError('');
      } else {
        setError('입력하신 정보와 일치하는 회원을 찾을 수 없습니다.');
      }
    } catch (err: any) {
      console.error('사용자 정보 확인 오류:', err);
      setError(err.response?.data?.detail || '입력하신 정보와 일치하는 회원을 찾을 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetData.newPassword.trim()) {
      setError('새 비밀번호를 입력해주세요.');
      return;
    }
    
    if (resetData.newPassword.length < 6) {
      setError('비밀번호는 최소 6자리 이상이어야 합니다.');
      return;
    }
    
    if (resetData.newPassword !== resetData.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // 전화번호에서 하이픈 제거
      const phoneWithoutHyphen = verifyData.phone.replace(/-/g, '');
      
      const response = await apiClient.post('/auth/reset-password', {
        user_id: verifyData.user_id,
        name: verifyData.name,
        phone: phoneWithoutHyphen,
        birth_date: verifyData.birthDate,
        new_password: resetData.newPassword
      });
      
      if (response.data.success) {
        setSuccess('비밀번호가 성공적으로 변경되었습니다. 로그인 페이지로 이동합니다.');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError('비밀번호 변경에 실패했습니다.');
      }
    } catch (err: any) {
      console.error('비밀번호 변경 오류:', err);
      setError(err.response?.data?.detail || '비밀번호 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PasswordResetContainer>
      <PasswordResetCard>
        <Title>비밀번호 재발급</Title>
        
        {step === 'verify' ? (
          <>
            <Notice>
              <NoticeText>
                비밀번호를 재발급하기 위해 본인 확인이 필요합니다.
                <br />
                아이디, 이름, 전화번호, 생년월일을 입력해주세요.
              </NoticeText>
            </Notice>
            
            <Form onSubmit={handleVerify}>
              <Input
                label="아이디"
                type="text"
                value={verifyData.user_id}
                onChange={(e) => handleVerifyInputChange('user_id', e.target.value)}
                placeholder="아이디를 입력하세요"
                required
              />
              
              <Input
                label="이름"
                type="text"
                value={verifyData.name}
                onChange={(e) => handleVerifyInputChange('name', e.target.value)}
                placeholder="이름을 입력하세요"
                required
              />
              
              <Input
                label="전화번호"
                type="tel"
                value={verifyData.phone}
                onChange={(e) => handleVerifyInputChange('phone', e.target.value)}
                placeholder="010-0000-0000"
                required
              />
              
              <Input
                label="생년월일"
                type="text"
                value={verifyData.birthDate}
                onChange={(e) => handleVerifyInputChange('birthDate', e.target.value)}
                placeholder="6자리 (예: 231105)"
                required
              />
              
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && <SuccessMessage>{success}</SuccessMessage>}
              
              <Button
                type="submit"
                variant="primary"
                size="large"
                fullWidth
                disabled={loading}
              >
                {loading ? '확인 중...' : '본인 확인'}
              </Button>
            </Form>
          </>
        ) : (
          <>
            <Notice>
              <NoticeText>
                본인 확인이 완료되었습니다.
                <br />
                새로운 비밀번호를 입력해주세요.
              </NoticeText>
            </Notice>
            
            <Form onSubmit={handleReset}>
              <Input
                label="새 비밀번호"
                type="password"
                value={resetData.newPassword}
                onChange={(e) => handleResetInputChange('newPassword', e.target.value)}
                placeholder="최소 6자리 이상"
                required
              />
              
              <Input
                label="새 비밀번호 확인"
                type="password"
                value={resetData.confirmPassword}
                onChange={(e) => handleResetInputChange('confirmPassword', e.target.value)}
                placeholder="비밀번호를 다시 입력하세요"
                required
              />
              
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && <SuccessMessage>{success}</SuccessMessage>}
              
              <Button
                type="submit"
                variant="primary"
                size="large"
                fullWidth
                disabled={loading}
              >
                {loading ? '변경 중...' : '비밀번호 변경'}
              </Button>
            </Form>
          </>
        )}
        
        <BackButton onClick={() => navigate('/')}>
          돌아가기
        </BackButton>
      </PasswordResetCard>
    </PasswordResetContainer>
  );
};

export default PasswordReset;

